import { Button, Classes, Tab, Tabs } from '@blueprintjs/core';
import * as React from 'react';
import { isEqual } from 'vet-bones/utils';

import { ColorPicker } from 'src/components/ColorPicker';
import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import { SelectableLinks } from 'src/components/SelectableLinks';
import {
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectLinkTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { ProjectDiagramAttributesTable } from 'src/containers/ProjectDiagram/components/attributesTable/ProjectDiagramAttributesTable';
import { ProjectDiagramClassDrawerGeospatialTab } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerGeospatialInputTab';
import { ProjectDiagramClassDrawerRelationships } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerRelationships';
import { ClassDrawerTab } from 'src/containers/ProjectDiagram/components/classDrawer/types';
import { ProjectDiagramEditableIri } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableIri';
import { ProjectDiagramEditableTextArea } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableTextArea';
import { useAddModelToClass } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useAddModelToClass';
import { useClickModelConceptAttributeLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptAttributeLink';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { useClickModelConceptRelationshipLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptRelationshipLink';
import { useCreateModelConceptClassFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptClassFromQuery';
import {
  useAddAttributeToClass,
  useRemoveAttributeFromClass,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptAttributeForClass';
import {
  useAddHierarchyForClass,
  useRemoveHierarchyForClass,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptHierarchyForClass';
import {
  useAddRelationshipForClass,
  useRemoveRelationshipForClass,
  useUpdateRelationshipForClass,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptRelationshipForClass';
import { useModelSelectItems } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelSelectItems';
import { useUpdateAttribute } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateAttribute';
import { useUpdateColorId } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateColorId';
import { useUpdateDescriptionForIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateDescriptionForIri';
import { useUpdateGeoData } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateGeoData';
import { useUpdateIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateIri';
import { useUpdateLabel } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useUpdateLabel';
import { useRemoveModelConceptClass } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelConceptClass';
import { useRemoveModelForIri } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveModelForIri';
import { ModelConceptClassHierarchy } from 'src/containers/ProjectDiagram/types/data';
import { isModelConceptClass } from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getConnectedHierarchyDataForClass } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/getConnectedHierarchyDataForClass';
import { classDraftChangesSelector } from 'src/selectors/draftClass';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { entityDrawerNodeDataSelector } from 'src/selectors/entityDrawerNodeData';
import { projectClassAttributeItemsSelector } from 'src/selectors/projectClassAttributesItems';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { projectClassRelationshipItemsSelector } from 'src/selectors/projectClassRelationshipItems';
import { projectColorIdsByIriSelector } from 'src/selectors/projectColorIdsByIri';
import { projectDescriptionsByIriSelector } from 'src/selectors/projectDescriptionsByIri';
import { projectGeoDataByIriSelector } from 'src/selectors/projectGeoDataByIri';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectLinkDataArraySelector } from 'src/selectors/projectLinkDataArray';
import { projectModelItemsSelector } from 'src/selectors/projectModelItems';
import { EntityDrawerType } from 'src/store/ui/types';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramClassDrawer: React.VFC = () => {
  const { projectId } = useRouterParams();
  const drawer = useReduxSelector(entityDrawerSelector);
  const [selectedTabId, setSelectedTabId] = React.useState(
    ClassDrawerTab.BASIC
  );

  const nodeData = useReduxSelector(
    entityDrawerNodeDataSelector(projectId, isModelConceptClass),
    isEqual
  );

  const classIri = nodeData?.value || '';

  const isEditingAttributes =
    drawer.type === EntityDrawerType.CLASS && drawer?.isEditingAttributes;

  const allClassItems = useReduxSelector(
    projectClassItemsSelector(projectId),
    isEqual
  );

  const allModelItems = useReduxSelector(
    projectModelItemsSelector(projectId),
    isEqual
  );

  const colorIdsByIri = useReduxSelector(
    projectColorIdsByIriSelector(projectId),
    isEqual
  );

  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );

  const descriptionsByIri = useReduxSelector(
    projectDescriptionsByIriSelector(projectId),
    isEqual
  );

  const linkDataArray = useReduxSelector(
    projectLinkDataArraySelector(projectId),
    isEqual
  );

  const {
    addedChildren,
    addedParents,
    isColorIdModified,
    isDescriptionModified,
    isLabelModified,
  } = useReduxSelector(classDraftChangesSelector(projectId, classIri), isEqual);

  const { availableModels, currentModels } = useModelSelectItems(classIri);

  const hierarchySelectItems = React.useMemo(() => {
    const parentIris = new Set<string>();
    const childIris = new Set<string>();

    const hierarchyDataArray = nodeData
      ? getConnectedHierarchyDataForClass({ linkDataArray }, nodeData.value)
      : [];

    hierarchyDataArray.forEach((linkData) => {
      const isParent = linkData.to === nodeData?.key;
      const isChild = linkData.from === nodeData?.key;
      const classIri = getModelConceptClassValue(
        isParent ? linkData.from : linkData.to
      );

      if (isParent) {
        childIris.add(classIri);
      }

      if (isChild) {
        parentIris.add(classIri);
      }
    });

    return {
      currentParents: allClassItems.filter((item) =>
        parentIris.has(item.value)
      ),
      availableParents: allClassItems.filter(
        (item) => !parentIris.has(item.value)
      ),
      currentChildren: allClassItems.filter((item) =>
        childIris.has(item.value)
      ),
      availableChildren: allClassItems.filter(
        (item) => !childIris.has(item.value)
      ),
    };
  }, [allClassItems, linkDataArray, nodeData]);

  const attributes = useReduxSelector(
    projectClassAttributeItemsSelector(projectId, classIri || '')
  );

  const geoDataByIri = useReduxSelector(projectGeoDataByIriSelector(projectId));

  const relationships = useReduxSelector(
    projectClassRelationshipItemsSelector(projectId, classIri, nodeData)
  );

  const handleUpdateIri = useUpdateIri(classIri);

  const handleUpdateLabel = useUpdateLabel(classIri);

  const handleUpdateColorId = useUpdateColorId(classIri);

  const handleUpdateDescription = useUpdateDescriptionForIri(classIri);

  const handleRemoveModel = useRemoveModelForIri(classIri);

  const handleAddModel = useAddModelToClass(classIri);

  const handleRemoveClass = useRemoveModelConceptClass();

  // hierarchies
  const handleClickClass = useClickModelConceptClassLink();

  const createNewClassFromQuery = useCreateModelConceptClassFromQuery();

  const handleAddParent = useAddHierarchyForClass({
    nodeData,
    type: ModelConceptClassHierarchy.PARENTS,
  });

  const handleAddChild = useAddHierarchyForClass({
    nodeData,
    type: ModelConceptClassHierarchy.CHILDREN,
  });

  const handleRemoveParent = useRemoveHierarchyForClass({
    nodeData,
    type: ModelConceptClassHierarchy.PARENTS,
  });

  const handleRemoveChild = useRemoveHierarchyForClass({
    nodeData,
    type: ModelConceptClassHierarchy.CHILDREN,
  });

  // relationships
  const handleAddRelationship = useAddRelationshipForClass({ classIri });

  const handleClickRelationship = useClickModelConceptRelationshipLink();

  const handleUnlinkRelationship = useRemoveRelationshipForClass({ classIri });

  const handleUpdateRelationship = useUpdateRelationshipForClass({ classIri });

  // attributes
  const handleAddAttribute = useAddAttributeToClass(classIri);

  const handleClickAttributeLink = useClickModelConceptAttributeLink();

  const handleUnlinkAttribute = useRemoveAttributeFromClass(classIri);

  const handleUpdateAttribute = useUpdateAttribute();

  // geo data
  const handleUpdateGeoData = useUpdateGeoData(classIri);

  if (!nodeData) {
    return null;
  }

  const colorId = colorIdsByIri[nodeData.value] || '';
  const descriptionForIri = descriptionsByIri[nodeData.value] || '';
  const label = labelsByIri[nodeData.value] || '';

  if (isEditingAttributes) {
    return (
      <div className={Classes.DRAWER_BODY}>
        <ProjectDiagramAttributesTable
          attributes={attributes}
          onAddAttribute={handleAddAttribute}
          onClickLink={handleClickAttributeLink}
          onUnlinkAttribute={handleUnlinkAttribute}
          onUpdateAttribute={handleUpdateAttribute}
        />
      </div>
    );
  }

  return (
    <>
      <div className={Classes.DRAWER_BODY}>
        <Tabs
          animate={false}
          onChange={(tabId: string) =>
            setSelectedTabId(tabId as ClassDrawerTab)
          }
          renderActiveTabPanelOnly
          selectedTabId={selectedTabId}
        >
          <Tab
            data-testid={TEST_IDS.projectDiagram.entityDrawer.tabs.basic}
            id={ClassDrawerTab.BASIC}
            panel={
              <div className="sd-drawer-project-diagram-entity-tab-content">
                <ProjectDiagramEditableIri
                  header={
                    isEditingAttributes
                      ? copy.components.projectDiagram.classDrawer
                          .nameWithAttributes
                      : copy.components.projectDiagram.classDrawer.name
                  }
                  iri={nodeData.value}
                  isHighlighted={isLabelModified}
                  label={label}
                  onSetIri={handleUpdateIri}
                  onSetLabel={handleUpdateLabel}
                  placeholder={
                    label === ''
                      ? copy.components.projectDiagram.modelConceptClass.new
                      : ''
                  }
                  rightEl={
                    <ColorPicker
                      colorId={colorId}
                      highlighted={isColorIdModified}
                      onSetColorId={handleUpdateColorId}
                    />
                  }
                  testId={TEST_IDS.projectDiagram.entityDrawer.name}
                />
                <ProjectDiagramEditableTextArea
                  // key here to description changes when you change from drawer to drawer
                  key={`${nodeData.value}-description`}
                  header={
                    copy.components.projectDiagram.entityDrawer.description
                  }
                  isHighlighted={isDescriptionModified}
                  onSetValue={handleUpdateDescription}
                  value={descriptionForIri}
                />

                {allModelItems.length > 1 ? (
                  <SelectableLinks
                    allowEmpty={false}
                    buttonText=""
                    filterTestId={
                      TEST_IDS.projectDiagram.entityDrawer.selectorFilter
                    }
                    header={copy.components.projectDiagram.entityDrawer.models}
                    items={availableModels}
                    itemTestId={(value, idx) =>
                      getProjectDiagramEntityDrawerSelectLinkTestId(
                        value,
                        'models',
                        idx
                      )
                    }
                    onRemove={handleRemoveModel}
                    onSelect={handleAddModel}
                    removeDisabledTooltip={
                      copy.components.projectDiagram.entityDrawer
                        .modelsRemoveDisabled
                    }
                    selected={currentModels}
                    selectorItemTestId={
                      getProjectDiagramEntityDrawerSelectItemTestId
                    }
                    selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
                      'models'
                    )}
                  />
                ) : null}
              </div>
            }
            title={copy.components.projectDiagram.classDrawer.tabs.basic}
          />
          <Tab
            data-testid={TEST_IDS.projectDiagram.entityDrawer.tabs.hierarchy}
            id={ClassDrawerTab.HIERARCHY}
            panel={
              <div className="sd-drawer-project-diagram-entity-columns">
                <SelectableLinks
                  buttonText=""
                  creatable
                  createNewItemFromQuery={createNewClassFromQuery}
                  createNewItemRenderer={createNewSelectItemRenderer}
                  createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
                    ''
                  )}
                  filterTestId={
                    TEST_IDS.projectDiagram.entityDrawer.selectorFilter
                  }
                  header={
                    copy.components.projectDiagram.classDrawer.parentClasses
                  }
                  highlightedValues={addedParents}
                  items={hierarchySelectItems.availableParents}
                  itemTestId={(value, idx) =>
                    getProjectDiagramEntityDrawerSelectLinkTestId(
                      value,
                      ModelConceptClassHierarchy.PARENTS,
                      idx
                    )
                  }
                  onClick={handleClickClass}
                  onRemove={handleRemoveParent}
                  onSelect={handleAddParent}
                  selected={hierarchySelectItems.currentParents}
                  selectorItemTestId={
                    getProjectDiagramEntityDrawerSelectItemTestId
                  }
                  selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
                    ModelConceptClassHierarchy.PARENTS
                  )}
                />
                <SelectableLinks
                  buttonText=""
                  creatable
                  createNewItemFromQuery={createNewClassFromQuery}
                  createNewItemRenderer={createNewSelectItemRenderer}
                  createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
                    ''
                  )}
                  filterTestId={
                    TEST_IDS.projectDiagram.entityDrawer.selectorFilter
                  }
                  header={
                    copy.components.projectDiagram.classDrawer.childClasses
                  }
                  highlightedValues={addedChildren}
                  items={hierarchySelectItems.availableChildren}
                  itemTestId={(value, idx) =>
                    getProjectDiagramEntityDrawerSelectLinkTestId(
                      value,
                      ModelConceptClassHierarchy.CHILDREN,
                      idx
                    )
                  }
                  onClick={handleClickClass}
                  onRemove={handleRemoveChild}
                  onSelect={handleAddChild}
                  selected={hierarchySelectItems.currentChildren}
                  selectorItemTestId={
                    getProjectDiagramEntityDrawerSelectItemTestId
                  }
                  selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
                    ModelConceptClassHierarchy.CHILDREN
                  )}
                />
              </div>
            }
            title={copy.components.projectDiagram.classDrawer.tabs.hierarchy}
          />
          <Tab
            data-testid={
              TEST_IDS.projectDiagram.entityDrawer.tabs.relationships
            }
            id={ClassDrawerTab.RELATIONSHIPS}
            panel={
              <ProjectDiagramClassDrawerRelationships
                onAddRelationship={handleAddRelationship}
                onClickRelationship={handleClickRelationship}
                onUnlinkRelationship={handleUnlinkRelationship}
                onUpdateRelationship={handleUpdateRelationship}
                relationships={relationships}
              />
            }
            title={
              copy.components.projectDiagram.classDrawer.tabs.relationships
            }
          />
          <Tab
            data-testid={TEST_IDS.projectDiagram.entityDrawer.tabs.attributes}
            id={ClassDrawerTab.ATTRIBUTES}
            panel={
              <ProjectDiagramAttributesTable
                attributes={attributes}
                onAddAttribute={handleAddAttribute}
                onClickLink={handleClickAttributeLink}
                onUnlinkAttribute={handleUnlinkAttribute}
                onUpdateAttribute={handleUpdateAttribute}
              />
            }
            title={copy.components.projectDiagram.classDrawer.tabs.attributes}
          />
          <Tab
            data-testid={TEST_IDS.projectDiagram.entityDrawer.tabs.geospatial}
            id={ClassDrawerTab.GEOSPATIAL}
            panel={
              <ProjectDiagramClassDrawerGeospatialTab
                attributeItems={attributes}
                geoDataByIri={geoDataByIri[classIri] || {}}
                onUpdateGeoData={handleUpdateGeoData}
              />
            }
            title={copy.components.projectDiagram.classDrawer.tabs.geospatial}
          />
        </Tabs>
      </div>

      <div className={Classes.DRAWER_FOOTER}>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectDiagram.entityDrawer.removeButton}
            onClick={() => handleRemoveClass({ key: nodeData.key })}
            outlined
          >
            {copy.components.projectDiagram.entityDrawerButtons.remove}
          </Button>
        </div>
      </div>
    </>
  );
};
